
import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';

interface User {
  ID: number;
  Name: string;
  Email: string;
  CreateTime: string;
}

interface UserTier {
  id?: number;
  tier_level: number;
  tier_name: string;
  trial_start_date?: string;
  trial_expires_at?: string;
  trial_used?: boolean;
}

interface AdminStatus {
  is_admin: boolean;
  admin_level: number;
  admin_role: string;
}

interface TrialStatus {
  isActive: boolean;
  daysRemaining: number;
  hasStarted: boolean;
  canStart: boolean;
}

interface AuthContextType {
  user: User | null;
  userTier: UserTier | null;
  adminStatus: AdminStatus;
  trialStatus: TrialStatus;
  isLoading: boolean;
  isInitialized: boolean;
  apisReady: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  hasAIAccess: (level: 'basic' | 'advanced') => boolean;
  canAccessFeature: (featureName: string) => boolean;
  startTrial: () => Promise<void>;
  login: (credentials: {email: string;password: string;}) => Promise<void>;
  register: (userData: {name: string;email: string;password: string;}) => Promise<void>;
  logout: () => Promise<void>;
  sendResetPasswordEmail: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  refreshSuperAdminStatus: () => Promise<void>;
  refreshAdminStatus: () => Promise<void>;
  refreshUserInfo: () => Promise<void>;
  retryInitialization: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Check if EZSite APIs are available
const areAPIsAvailable = (): boolean => {
  return typeof window !== 'undefined' &&
  window.ezsite &&
  window.ezsite.apis &&
  typeof window.ezsite.apis.getUserInfo === 'function';
};

// Wait for APIs to be available with better timeout handling
const waitForAPIs = (maxWait: number = 10000): Promise<boolean> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.floor(maxWait / 200);

    const checkAPIs = () => {
      attempts++;
      console.log(`Checking for APIs... Attempt ${attempts}/${maxAttempts}`);

      if (areAPIsAvailable()) {
        console.log('APIs are available!');
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= maxWait || attempts >= maxAttempts) {
        console.warn('APIs not available after maximum wait time. Continuing without APIs.');
        resolve(false);
        return;
      }

      setTimeout(checkAPIs, 200);
    };

    checkAPIs();
  });
};

// Calculate trial status
const calculateTrialStatus = (tierData?: UserTier): TrialStatus => {
  const defaultStatus: TrialStatus = {
    isActive: false,
    daysRemaining: 0,
    hasStarted: false,
    canStart: true
  };

  if (!tierData || tierData.tier_level !== 1) {
    return { ...defaultStatus, canStart: false };
  }

  if (!tierData.trial_start_date || !tierData.trial_expires_at || tierData.trial_start_date === '' || tierData.trial_expires_at === '') {
    return { ...defaultStatus, canStart: !tierData.trial_used };
  }

  const now = new Date();
  const expiryDate = new Date(tierData.trial_expires_at);
  const startDate = new Date(tierData.trial_start_date);

  const isActive = now < expiryDate && startDate <= now;
  const timeDiff = expiryDate.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));

  return {
    isActive,
    daysRemaining: daysLeft,
    hasStarted: startDate <= now,
    canStart: false
  };
};

export const AuthProvider: React.FC<{children: React.ReactNode;}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userTier, setUserTier] = useState<UserTier | null>(null);
  const [adminStatus, setAdminStatus] = useState<AdminStatus>({
    is_admin: false,
    admin_level: 0,
    admin_role: ''
  });
  const [trialStatus, setTrialStatus] = useState<TrialStatus>({
    isActive: false,
    daysRemaining: 0,
    hasStarted: false,
    canStart: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [apisReady, setApisReady] = useState(false);
  const [isSuperAdminState, setIsSuperAdminState] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  // Update trial status whenever userTier changes
  useEffect(() => {
    const newTrialStatus = calculateTrialStatus(userTier);
    setTrialStatus(newTrialStatus);

    // Auto-mark expired trials as used
    if (userTier && userTier.trial_expires_at && newTrialStatus.hasStarted && !newTrialStatus.isActive && !userTier.trial_used) {
      markTrialAsUsed(userTier);
    }
  }, [userTier]);

  const markTrialAsUsed = async (tierData: UserTier) => {
    try {
      if (!tierData.id) return;

      const { error } = await window.ezsite.apis.tableUpdate(15216, {
        ID: tierData.id,
        trial_used: true
      });

      if (!error) {
        setUserTier((prev) => prev ? { ...prev, trial_used: true } : null);
      }
    } catch (error) {
      console.error('Failed to mark trial as used:', error);
    }
  };

  const initializeAuth = async () => {
    try {
      console.log('Initializing authentication...');
      setIsLoading(true);
      setIsInitialized(false);

      // Wait for APIs to be available (reduced timeout)
      const apisAvailable = await waitForAPIs(8000);
      setApisReady(apisAvailable);

      if (!apisAvailable) {
        console.warn('EZSite APIs are not available. Authentication will work in limited mode.');
        // Don't throw error, just continue with limited functionality
        setIsLoading(false);
        setIsInitialized(true);
        return;
      }

      console.log('APIs ready, checking auth status...');
      // Now check auth status
      await checkAuthStatus();
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      // Always set initialized to true so the app can continue
      setApisReady(false);
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const retryInitialization = () => {
    console.log('Retrying authentication initialization...');
    initializeAuth();
  };

  const checkAuthStatus = async () => {
    try {
      if (!areAPIsAvailable()) {
        throw new Error('APIs not available');
      }

      console.log('Checking authentication status...');
      const { data, error } = await window.ezsite.apis.getUserInfo();
      if (error) {
        console.log('User not authenticated:', error);
        setUser(null);
        setUserTier(null);
        setAdminStatus({
          is_admin: false,
          admin_level: 0,
          admin_role: ''
        });
        setIsSuperAdminState(false);
      } else {
        console.log('User authenticated:', data);
        setUser(data);
        await Promise.all([
        fetchUserTier(data.ID),
        checkSuperAdminStatus(data.Email),
        checkAdminStatus(data.ID)]
        );
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setUserTier(null);
      setAdminStatus({
        is_admin: false,
        admin_level: 0,
        admin_role: ''
      });
      setIsSuperAdminState(false);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const fetchUserTier = async (userId: number) => {
    try {
      if (!areAPIsAvailable()) {
        throw new Error('APIs not available');
      }

      const response = await window.ezsite.apis.tablePage(15216, {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'user_id',
          op: 'Equal',
          value: userId
        }]

      });

      if (!response.error && response.data?.List && response.data.List.length > 0) {
        const tierData = response.data.List[0];
        setUserTier(tierData);

        // Auto-assign tier 1 to new users if not assigned
        if (!tierData.tier_level || tierData.tier_level === 0) {
          await createDefaultTier(userId);
        }
      } else {
        // Create default tier for new users
        await createDefaultTier(userId);
      }
    } catch (error) {
      console.error('Failed to fetch user tier:', error);
      // Default tier on error
      setUserTier({
        tier_level: 1,
        tier_name: 'Basic'
      });
    }
  };

  const createDefaultTier = async (userId: number) => {
    try {
      const now = new Date();
      const { error } = await window.ezsite.apis.tableCreate(15216, {
        user_id: userId,
        tier_level: 1,
        tier_name: 'Basic',
        trial_used: false,
        assigned_by: 0,
        assigned_at: now.toISOString(),
        trial_start_date: '',
        trial_expires_at: ''
      });

      if (!error) {
        setUserTier({
          tier_level: 1,
          tier_name: 'Basic',
          trial_used: false,
          trial_start_date: '',
          trial_expires_at: ''
        });
      }
    } catch (error) {
      console.error('Failed to create default tier:', error);
    }
  };

  const startTrial = async () => {
    if (!user || !userTier || userTier.tier_level !== 1 || trialStatus.hasStarted) return;

    try {
      const now = new Date();
      const expiryDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days from now

      const updateData: any = {
        trial_start_date: now.toISOString(),
        trial_expires_at: expiryDate.toISOString(),
        trial_used: false // Keep as false until trial expires
      };

      if (userTier.id) {
        updateData.ID = userTier.id;
        const { error } = await window.ezsite.apis.tableUpdate(15216, updateData);
        if (error) throw error;
      } else {
        // If no ID, try to find and update the record
        const response = await window.ezsite.apis.tablePage(15216, {
          PageNo: 1,
          PageSize: 1,
          Filters: [{ name: "user_id", op: "Equal", value: user.ID }]
        });

        if (response.data?.List && response.data.List.length > 0) {
          updateData.ID = response.data.List[0].ID;
          const { error } = await window.ezsite.apis.tableUpdate(15216, updateData);
          if (error) throw error;
        }
      }

      // Update local state
      setUserTier((prev) => prev ? {
        ...prev,
        trial_start_date: now.toISOString(),
        trial_expires_at: expiryDate.toISOString(),
        trial_used: false // Keep as false until trial expires
      } : null);

      toast({
        title: "Trial Started!",
        description: "You now have 7 days of premium access. Enjoy exploring all features!"
      });

    } catch (error) {
      console.error('Error starting trial:', error);
      toast({
        title: "Error",
        description: "Failed to start trial. Please try again.",
        variant: "destructive"
      });
    }
  };

  const checkAdminStatus = async (userId: number) => {
    try {
      if (!areAPIsAvailable()) {
        throw new Error('APIs not available');
      }

      // Check admin_users table for admin privileges
      const response = await window.ezsite.apis.tablePage(15992, {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'user_id',
          op: 'Equal',
          value: userId
        },
        {
          name: 'is_active',
          op: 'Equal',
          value: true
        }]

      });

      if (!response.error && response.data?.List && response.data.List.length > 0) {
        const adminData = response.data.List[0];
        setAdminStatus({
          is_admin: true,
          admin_level: adminData.admin_level || 1,
          admin_role: adminData.admin_role || 'Admin'
        });
      } else {
        setAdminStatus({
          is_admin: false,
          admin_level: 0,
          admin_role: ''
        });
      }
    } catch (error) {
      console.error('Failed to check admin status:', error);
      setAdminStatus({
        is_admin: false,
        admin_level: 0,
        admin_role: ''
      });
    }
  };

  const checkSuperAdminStatus = async (email: string) => {
    try {
      if (!areAPIsAvailable()) {
        throw new Error('APIs not available');
      }

      // Check hardcoded emails first (backward compatibility)
      const hardcodedSuperAdmins = [
      'admin@teachai.com',
      'admin@ezsite.ai',
      'support@ezsite.ai'];


      if (hardcodedSuperAdmins.includes(email)) {
        setIsSuperAdminState(true);
        return;
      }

      // Check app_settings table for dynamic superadmin assignments
      const settingKey = `superadmin_email_${email.replace('@', '_at_').replace('.', '_dot_')}`;

      const response = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        {
          name: 'setting_key',
          op: 'Equal',
          value: settingKey
        }]

      });

      if (!response.error && response.data?.List && response.data.List.length > 0) {
        setIsSuperAdminState(true);
      } else {
        setIsSuperAdminState(false);
      }

    } catch (error) {
      console.error('Failed to check superadmin status:', error);
      setIsSuperAdminState(false);
    }
  };

  const refreshSuperAdminStatus = async () => {
    if (user?.Email) {
      await checkSuperAdminStatus(user.Email);
    }
  };

  const refreshAdminStatus = async () => {
    if (user?.ID) {
      await checkAdminStatus(user.ID);
    }
  };

  const isSuperAdmin = isSuperAdminState;
  const isAdmin = adminStatus.is_admin || isSuperAdmin; // Super admins are also considered admins

  const hasAIAccess = (level: 'basic' | 'advanced'): boolean => {
    if (isSuperAdmin || isAdmin) return true;

    if (!userTier) return false;

    if (level === 'basic') {
      // Tier 1 users have access during trial period or can start trial
      if (userTier.tier_level === 1) {
        return trialStatus.isActive || trialStatus.canStart;
      }
      return userTier.tier_level >= 1;
    } else if (level === 'advanced') {
      // Tier 1 users have access during trial period only
      if (userTier.tier_level === 1) {
        return trialStatus.isActive;
      }
      return userTier.tier_level >= 2;
    }

    return false;
  };

  const canAccessFeature = (featureName: string): boolean => {
    // Super admins and admins always have access
    if (isSuperAdmin || isAdmin) return true;

    // Tier 2+ users always have access
    if (userTier?.tier_level >= 2) return true;

    // Tier 1 users can access during active trial or can start trial
    if (userTier?.tier_level === 1) {
      return trialStatus.isActive || trialStatus.canStart;
    }

    return false;
  };

  const login = async (credentials: {email: string;password: string;}) => {
    setIsLoading(true);
    try {
      if (!apisReady || !areAPIsAvailable()) {
        throw new Error('Authentication system is not available. Please refresh the page and try again.');
      }

      console.log('Attempting login with credentials:', { email: credentials.email });

      const { error } = await window.ezsite.apis.login(credentials);
      if (error) {
        console.error('Login API error:', error);
        throw new Error(error);
      }

      // Get user info after successful login
      const { data: userData, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) {
        console.error('Get user info error:', userError);
        throw new Error(userError);
      }

      console.log('Login successful, user data:', userData);
      setUser(userData);
      await Promise.all([
      fetchUserTier(userData.ID),
      checkSuperAdminStatus(userData.Email),
      checkAdminStatus(userData.ID)]
      );

      toast({
        title: "Welcome back!",
        description: "You have successfully logged in."
      });
    } catch (error: any) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: {name: string;email: string;password: string;}) => {
    setIsLoading(true);
    try {
      if (!apisReady || !areAPIsAvailable()) {
        throw new Error('Registration system is not available. Please refresh the page and try again.');
      }

      console.log('Attempting registration with email:', userData.email);

      const { error } = await window.ezsite.apis.register({
        email: userData.email,
        password: userData.password
      });

      if (error) {
        console.error('Registration API error:', error);
        throw new Error(error);
      }

      toast({
        title: "Registration successful!",
        description: "Please check your email to verify your account before logging in."
      });

    } catch (error: any) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (!apisReady || !areAPIsAvailable()) {
        throw new Error('Logout system is not available.');
      }

      const { error } = await window.ezsite.apis.logout();
      if (error) throw new Error(error);

      setUser(null);
      setUserTier(null);
      setAdminStatus({
        is_admin: false,
        admin_level: 0,
        admin_role: ''
      });
      setIsSuperAdminState(false);
      toast({
        title: "Logged out",
        description: "You have been successfully logged out."
      });
    } catch (error: any) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const sendResetPasswordEmail = async (email: string) => {
    try {
      if (!apisReady || !areAPIsAvailable()) {
        throw new Error('Password reset system is not available. Please refresh the page and try again.');
      }

      const { error } = await window.ezsite.apis.sendResetPwdEmail({ email });
      if (error) throw new Error(error);

      toast({
        title: "Reset email sent",
        description: "Please check your email for password reset instructions."
      });
    } catch (error: any) {
      console.error('Password reset email failed:', error);
      throw error;
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      if (!apisReady || !areAPIsAvailable()) {
        throw new Error('Password reset system is not available. Please refresh the page and try again.');
      }

      const { error } = await window.ezsite.apis.resetPassword({ token, password });
      if (error) throw new Error(error);

      toast({
        title: "Password reset successful",
        description: "Your password has been updated successfully."
      });
    } catch (error: any) {
      console.error('Password reset failed:', error);
      throw error;
    }
  };

  const refreshUserInfo = async () => {
    if (user) {
      await fetchUserTier(user.ID);
    }
  };

  const value = {
    user,
    userTier,
    adminStatus,
    trialStatus,
    isLoading,
    isInitialized,
    apisReady,
    isSuperAdmin,
    isAdmin,
    hasAIAccess,
    canAccessFeature,
    startTrial,
    login,
    register,
    logout,
    sendResetPasswordEmail,
    resetPassword,
    refreshSuperAdminStatus,
    refreshAdminStatus,
    refreshUserInfo,
    retryInitialization
  };

  return <AuthContext.Provider value={value} data-id="7twx6e920" data-path="src/contexts/AuthContext.tsx">{children}</AuthContext.Provider>;
};